import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import {
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    ListItemIcon,
    Box,
    IconButton,
    Typography,
    alpha,
    TextField,
    InputAdornment,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import CloseIcon from '@mui/icons-material/Close';
import PlaceIcon from '@mui/icons-material/Place';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import { useTranslation } from 'react-i18next';
import { AppContext, SttFormSearch, SttNoResults } from '../all';

type City = {
    id: number;
    name: string;
};

type Province = {
    id: number;
    name: string;
};

type Props = {
    grid?: boolean;
    search?: boolean;
    caption: string;
    name: string;
    idProvince?: number;
    defVal: string;
    onChange: (event: { name: string | undefined; value: unknown }) => void;
    md?: number;
    xs?: number;
    onSelect: (event: { name: string; value: City | null }) => void;
    disabled?: boolean;
    [key: string]: unknown;
};

/**
 *    Select city with optional province filter
 *
 */
export function SttSelectCity({
    grid = true,
    caption,
    name,
    idProvince: initialIdProvince,
    defVal,
    onChange,
    md = 12,
    xs = 12,
    onSelect,
    disabled,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const [dialogOpened, setDialogOpened] = useState(false);
    const [cities, setCities] = useState<City[]>([]);
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [selectedProvinceId, setSelectedProvinceId] = useState<number | null>(
        initialIdProvince || null,
    );
    const [totalCount, setTotalCount] = useState(0);
    const [keywordSearch, setKeywordSearch] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [loadingProvinces, setLoadingProvinces] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const listRef = useRef<HTMLUListElement>(null);

    const ITEMS_PER_PAGE = 300;

    // Load provinces when dialog opens
    useEffect(() => {
        if (dialogOpened && provinces.length === 0) {
            loadProvinces();
        }
    }, [dialogOpened]);

    // Load cities when dialog opens, province changes, or search changes
    useEffect(() => {
        if (dialogOpened) {
            // Reset and load first page
            setCities([]);
            setPage(1);
            setHasMore(true);
            loadCities(1, keywordSearch, selectedProvinceId, true);
        }
    }, [dialogOpened, selectedProvinceId]);

    // Update selected province when prop changes
    useEffect(() => {
        if (initialIdProvince !== undefined) {
            setSelectedProvinceId(initialIdProvince);
        }
    }, [initialIdProvince]);

    /**
     * Load provinces from API
     */
    const loadProvinces = useCallback(() => {
        setLoadingProvinces(true);
        cxt?.api('GET', `/provinces`, {
            params: {
                rows: 100000,
            },
            success: (r: { rows: Province[] }) => {
                setProvinces(r.rows);
                setLoadingProvinces(false);
            },
            error: () => {
                setLoadingProvinces(false);
            },
        });
    }, [cxt]);

    /**
     * Load cities with pagination and optional province filter
     */
    const loadCities = useCallback(
        (
            pageNum: number,
            searchName: string,
            provinceId: number | null,
            reset = false,
        ) => {
            if (loading) return;

            setLoading(true);
            cxt?.api('GET', `/cities`, {
                params: {
                    page: pageNum,
                    rows: ITEMS_PER_PAGE,
                    name: searchName,
                    idProvince: provinceId || '',
                },
                success: (r: {
                    count: number;
                    rows: City[];
                    pages: number;
                }) => {
                    if (reset) {
                        setCities(r.rows);
                    } else {
                        setCities((prev) => [...prev, ...r.rows]);
                    }
                    setTotalCount(r.count);
                    setPage(pageNum);
                    setHasMore(
                        r.rows.length === ITEMS_PER_PAGE && pageNum < r.pages,
                    );
                    setLoading(false);
                },
                error: () => {
                    setLoading(false);
                },
            });
        },
        [cxt, loading],
    );

    /**
     * Handle scroll to load more
     */
    const handleScroll = useCallback(() => {
        if (!listRef.current || loading || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = listRef.current;
        // Load more when scrolled to 80% of the list
        if (scrollTop + clientHeight >= scrollHeight * 0.8) {
            loadCities(page + 1, keywordSearch, selectedProvinceId, false);
        }
    }, [loading, hasMore, page, keywordSearch, selectedProvinceId, loadCities]);

    /**
     * Handle province selection change
     */
    const handleProvinceChange = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        const value = event.target.value as string;
        const provinceId = value === '' ? null : Number(value);
        setSelectedProvinceId(provinceId);
    };

    /**
     * Handle search input change
     */
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setKeywordSearch(value);
    };

    /**
     * Handle search submit
     */
    const handleSearchSubmit = () => {
        setCities([]);
        setPage(1);
        setHasMore(true);
        loadCities(1, keywordSearch, selectedProvinceId, true);
    };

    /**
     * Handle key press in search
     */
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearchSubmit();
        }
    };

    /**
     * Click search
     */
    const handleClickSearch = () => {
        if (disabled) {
            return;
        }
        setDialogOpened(true);
    };

    /**
     * Close dialog
     */
    const onClose = () => {
        setDialogOpened(false);
    };

    /**
     * Select
     */
    const _onSelect = (p: { name: string; value: City }) => {
        setDialogOpened(false);
        onSelect(p);
    };

    const content = (
        <>
            <SttFormSearch
                disabled
                caption={caption}
                defVal={defVal}
                showClearButton
                onClean={() => onSelect({ name, value: null })}
                onChange={onChange}
                onClickSearch={handleClickSearch}
            />

            <Dialog
                fullWidth
                maxWidth="sm"
                onClose={onClose}
                open={dialogOpened}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        background: (theme) =>
                            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        // Tinta sobre lima: el blanco no se lee sobre el
                        // degradado de marca.
                        color: 'primary.contrastText',
                        py: 2,
                        px: 3,
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <LocationCityIcon />
                            <Typography
                                variant="h6"
                                component="span"
                                fontWeight={600}
                            >
                                {caption}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{
                                color: 'primary.contrastText',
                                '&:hover': {
                                    backgroundColor: 'rgba(18, 21, 15, 0.1)',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <Box className="border-border border-b p-4">
                    {/* Province Filter */}
                    <FormControl
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        disabled={loadingProvinces}
                    >
                        <InputLabel id="province-filter-label">
                            {t('generic.province')}
                        </InputLabel>
                        <Select
                            labelId="province-filter-label"
                            id="province-filter"
                            value={selectedProvinceId?.toString() || ''}
                            label={t('generic.province')}
                            onChange={handleProvinceChange as any}
                            startAdornment={
                                <InputAdornment position="start">
                                    <PublicIcon
                                        sx={{
                                            color: 'text.secondary',
                                            ml: 0.5,
                                        }}
                                    />
                                </InputAdornment>
                            }
                            className="bg-muted hover:bg-input [&_fieldset]:border-border rounded-2xl"
                        >
                            <MenuItem value="">
                                <em>{t('common.none')}</em>
                            </MenuItem>
                            {provinces.map((province) => (
                                <MenuItem key={province.id} value={province.id}>
                                    {province.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Search Input */}
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={t('common.search') + '...'}
                        value={keywordSearch}
                        onChange={handleSearchChange}
                        onKeyPress={handleKeyPress}
                        autoFocus
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{ color: 'text.secondary' }}
                                    />
                                </InputAdornment>
                            ),
                            className:
                                'bg-muted hover:bg-input [&_fieldset]:border-none rounded-2xl',
                        }}
                    />
                </Box>

                <DialogContent sx={{ p: 0, maxHeight: 400 }}>
                    <List
                        ref={listRef}
                        onScroll={handleScroll}
                        sx={{
                            py: 0,
                            maxHeight: 400,
                            overflow: 'auto',
                        }}
                    >
                        {cities.map((city, idx) => (
                            <ListItemButton
                                key={city.id || idx}
                                onClick={() => _onSelect({ name, value: city })}
                                sx={{
                                    py: 1.5,
                                    px: 3,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: (theme) =>
                                            alpha(
                                                theme.palette.primary.main,
                                                0.08,
                                            ),
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                    <PlaceIcon
                                        sx={{
                                            color: 'var(--brand-text)',
                                            fontSize: '1.3rem',
                                        }}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={city.name}
                                    primaryTypographyProps={{
                                        fontWeight: 500,
                                        fontSize: '0.95rem',
                                    }}
                                />
                            </ListItemButton>
                        ))}

                        {loading && (
                            <ListItem sx={{ py: 2, justifyContent: 'center' }}>
                                <CircularProgress size={24} />
                            </ListItem>
                        )}

                        {!loading && cities.length === 0 && (
                            <ListItem sx={{ py: 4 }}>
                                <Box
                                    width="100%"
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <LocationCityIcon
                                        sx={{
                                            fontSize: 48,
                                            color: 'text.disabled',
                                        }}
                                    />
                                    <SttNoResults />
                                </Box>
                            </ListItem>
                        )}
                    </List>

                    {cities.length > 0 && (
                        <Box className="border-border bg-muted flex justify-center border-t px-4 py-3">
                            <Typography variant="body2" color="text.secondary">
                                {t('common.showingOf', {
                                    shown: cities.length,
                                    total: totalCount,
                                })}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );

    // -----| Render |-----

    return grid ? <Grid size={{ xs, md }}>{content}</Grid> : <>{content}</>;
}
